import {USER_WAIT_DEADLINE_MS} from './userWaitDeadline';

const BRIDGE_VERSION = 1;

type MobileAppWindow = Window & {
    ReactNativeWebView?: {postMessage: (data: string) => void};
    __vkardMobileApp?: MobileAppBridgeApi;
};

// Cross-repo contract with the Expo app; keep in sync.
// `auth.request` asks the app for an access token from its own session, answered on `id` via
// `__vkardMobileApp.provideAccessToken` or `failAccessToken`; `force` asks for a newly
// refreshed one, whose claims reflect a change just made server side.
// `org.switch` asks the app to switch its session to `organizationId`, answered on `id` the
// same way with an access token for that organization.
// `auth.logout` tells the app the user logged out, so it logs out too.
// `share.request` asks the app to open the phone's share sheet for `url`.
// `contact.add` asks the app to add `vcard` to the phone's contacts.
// `file.download` asks the app to download `url`, readable without a session, and offer to
// save it on the phone or share it.
export type MobileAppBridgeMessage =
    | {type: 'ready'}
    | {type: 'auth.request'; id: string; force?: boolean}
    | {type: 'org.switch'; id: string; organizationId: string}
    | {type: 'auth.logout'}
    | {type: 'share.request'; url: string; title: string}
    | {type: 'contact.add'; vcard: string}
    | {type: 'file.download'; url: string; fileName?: string};

// Exposed on `window.__vkardMobileApp` for the app to call (native -> web).
// Cross-repo contract; keep in sync with the Expo app.
// `expiresIn` is how many seconds the token remains valid: the phone's clock may be off, so
// the dashboard does not compare the token's own expiry with it.
type MobileAppBridgeApi = {
    provideAccessToken: (
        id: string,
        accessToken: string,
        expiresIn: number,
    ) => void;
    failAccessToken: (id: string) => void;
};

export type MobileAppAccessToken = {
    accessToken: string;
    expiresIn: number;
};

// The app's WebViews add this to their user agent: `ReactNativeWebView` alone exists in
// any React Native app.
const MOBILE_APP_USER_AGENT = 'VKARDApp';

function getBridge() {
    if (typeof window === 'undefined') {
        return;
    }

    const w = window as MobileAppWindow;

    return w.navigator?.userAgent.includes(MOBILE_APP_USER_AGENT)
        ? w.ReactNativeWebView
        : undefined;
}

export function isInMobileApp(): boolean {
    return typeof getBridge() !== 'undefined';
}

// No-op outside the mobile app WebView, so it is safe to call from shared code.
export function notifyMobileApp(message: MobileAppBridgeMessage): void {
    const bridge = getBridge();
    if (!bridge) {
        return;
    }
    bridge.postMessage(JSON.stringify({v: BRIDGE_VERSION, ...message}));
}

// URL sentinel the mobile app watches for to show its own error screen.
const MOBILE_APP_ERROR_URL = '/?mobile-app=error';

let failureReported = false;
const failureListeners = new Set<() => void>();

// Once reported, the app's error screen owns recovery (its retry reloads the page).
// No-op outside the mobile app.
export function reportFailureToMobileApp(): void {
    if (!isInMobileApp()) {
        return;
    }

    failureReported = true;
    window.history.replaceState({}, document.title, MOBILE_APP_ERROR_URL);
    failureListeners.forEach(listener => listener());
}

export function hasReportedFailureToMobileApp(): boolean {
    return failureReported;
}

// Returns an unsubscribe.
export function onFailureReportedToMobileApp(listener: () => void): () => void {
    failureListeners.add(listener);

    return () => {
        failureListeners.delete(listener);
    };
}

type PendingTokenRequest = {
    resolve: (token: MobileAppAccessToken) => void;
    reject: (reason?: unknown) => void;
};

const pendingTokenRequests = new Map<string, PendingTokenRequest>();

function settleTokenRequest(
    id: string,
    settle: (pending: PendingTokenRequest) => void,
): void {
    const pending = pendingTokenRequests.get(id);
    if (!pending) {
        return;
    }
    pendingTokenRequests.delete(id);
    settle(pending);
}

// Installed before the first request is sent, so an answer never finds it missing.
function getBridgeApi(): MobileAppBridgeApi {
    const w = window as MobileAppWindow;
    w.__vkardMobileApp ??= {
        provideAccessToken: (id, accessToken, expiresIn) =>
            settleTokenRequest(id, ({resolve, reject}) =>
                typeof accessToken === 'string' &&
                Number.isFinite(expiresIn) &&
                expiresIn > 0
                    ? resolve({accessToken, expiresIn})
                    : reject(
                          new Error('Invalid access token from the mobile app'),
                      ),
            ),
        failAccessToken: id =>
            settleTokenRequest(id, ({reject}) =>
                reject(
                    new Error('Mobile app failed to provide an access token'),
                ),
            ),
    };

    return w.__vkardMobileApp;
}

let tokenRequestSeq = 0;

export class MobileAppTimeoutError extends Error {}

type TokenRequestMessage = Extract<
    MobileAppBridgeMessage,
    {type: 'auth.request'} | {type: 'org.switch'}
>;

// Rejects (rather than hangs) outside the app or on timeout, so callers can fall
// back to their own recovery.
function requestAccessTokenFromMobileApp(
    createMessage: (id: string) => TokenRequestMessage,
): Promise<MobileAppAccessToken> {
    if (!isInMobileApp()) {
        return Promise.reject(new Error('Not running inside the mobile app'));
    }

    getBridgeApi();
    const id = `tok_${++tokenRequestSeq}`;

    return new Promise<MobileAppAccessToken>((resolve, reject) => {
        const timer = setTimeout(() => {
            if (pendingTokenRequests.delete(id)) {
                reject(
                    new MobileAppTimeoutError(
                        'Mobile app access token request timed out',
                    ),
                );
            }
        }, USER_WAIT_DEADLINE_MS);

        pendingTokenRequests.set(id, {
            resolve: token => {
                clearTimeout(timer);
                resolve(token);
            },
            reject: reason => {
                clearTimeout(timer);
                reject(reason);
            },
        });

        notifyMobileApp(createMessage(id));
    });
}

export function requestMobileAppAccessToken({
    force = false,
}: {force?: boolean} = {}): Promise<MobileAppAccessToken> {
    return requestAccessTokenFromMobileApp(id => ({
        type: 'auth.request',
        id,
        force,
    }));
}

export function requestMobileAppOrganizationSwitch(
    organizationId: string,
): Promise<MobileAppAccessToken> {
    return requestAccessTokenFromMobileApp(id => ({
        type: 'org.switch',
        id,
        organizationId,
    }));
}
